import { useCallback } from "react";
import { TellescopeMutationInlineOptions } from "../../types";
import { useTellescopeMutation } from "../../use-tellescope-mutation";

/** Just the values needed from the caller on mutate */
export interface TwilioBuyTFNRequest {
  phoneNumber: string;
}

export interface TwilioBuyTFNResponse {
  data: {
    phoneNumber: string;
    sid: string;
    friendlyName: string;
  };
}

type BuyTFNInlineOptions = TellescopeMutationInlineOptions<"integrations", "proxy_write">;

/**
 * Purchases a Twilio toll-free number. The purchased number is optimistically attached to the org
 * (set as `twilioNumber` if none exists yet, otherwise appended to `twilioNumbers`) and the Twilio
 * `actions` query is invalidated on success — both handled by the standard mutation side-effect
 * mappings for the `integrations` model. The org update is rolled back automatically on error.
 */
export const useTwilioBuyTFNMutation = () => {
  const mutation = useTellescopeMutation({
    model: "integrations",
    method: "proxy_write",
    autoCacheUpdateStrategy: "update",
    optimisticUpdates: true,
  });

  const mutate = useCallback(
    (request: TwilioBuyTFNRequest, options?: BuyTFNInlineOptions) =>
      mutation.mutate({ integration: "Twilio", type: "buy-tfn", query: request }, options),
    [mutation]
  );

  const mutateAsync = useCallback(
    (request: TwilioBuyTFNRequest, options?: BuyTFNInlineOptions) =>
      mutation.mutateAsync({ integration: "Twilio", type: "buy-tfn", query: request }, options),
    [mutation]
  );

  return { ...mutation, mutate, mutateAsync };
};
